import { useMemo, useEffect, useRef } from 'react';
import { useAssessments } from './useAssessments';
import { usePersonalityOverrides } from './usePersonalityOverrides';
import { useCanonicalPersonality } from './useCanonicalPersonality';
import { useEnrichmentLayers } from './useEnrichmentLayers';
import { useConfidenceReport } from './useConfidenceReport';
import { useAuth } from './useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PersonalityProfile } from '@/types/tps.types';
import { calculateKeirsey } from '@/mappings/keirsey.mapping';
import { calculateHogwartsHouse } from '@/mappings/harryPotterHouses.mapping';
import { calculateLoveLanguages } from '@/mappings/loveLanguages.mapping';
import { calculateTrueColor } from '@/mappings/trueColors.mapping';
import { calculateTemperament } from '@/mappings/fourTemperaments.mapping';
import { calculateBigFiveEnhanced } from '@/mappings/bigfive.enhanced';
import { calculateAlignmentEnhanced } from '@/mappings/alignment.enhanced';
import { TPSScores } from '@/types/tps.types';
import { isCanonicalEvidenceFresh } from '@/utils/personalityOverridePrecedence';
import {
  MBTI_TO_SOCIONICS_MAPPING,
  TYPE_COGNITIVE_STACKS,
  deriveMbtiDerivatives,
  isSocionicsStale,
  rebuildMbtiDetail,
  normalizeMbtiCode,
} from '@/utils/mbtiDerivatives';
import { rebuildEnneagramDetail } from '@/utils/enneagramDerivatives';

// Helper to safely extract enneagram string from various formats
function normalizeEnneagram(enneagram: unknown): string | undefined {
  if (!enneagram) return undefined;
  if (typeof enneagram === 'string') return enneagram;
  if (typeof enneagram === 'object' && enneagram !== null) {
    const obj = enneagram as { type?: number; wing?: number };
    if (obj.type) {
      return obj.wing ? `${obj.type}w${obj.wing}` : `Type ${obj.type}`;
    }
  }
  return undefined;
}

// Helper to safely extract MBTI string
function normalizeMBTI(mbti: unknown): string | undefined {
  if (!mbti) return undefined;
  if (typeof mbti === 'string') return mbti;
  if (typeof mbti === 'object' && mbti !== null) {
    const obj = mbti as { type?: string };
    return obj.type || undefined;
  }
  return undefined;
}

// Helper to safely extract alignment string
function normalizeAlignment(alignment: unknown): string | undefined {
  if (!alignment) return undefined;
  if (typeof alignment === 'string') return alignment;
  if (typeof alignment === 'object' && alignment !== null) {
    const obj = alignment as { alignment?: string };
    return obj.alignment || undefined;
  }
  return undefined;
}

// Helper to safely extract Holland code
function normalizeHollandCode(holland: unknown): string | undefined {
  if (!holland) return undefined;
  if (typeof holland === 'string') return holland;
  if (typeof holland === 'object' && holland !== null) {
    const obj = holland as { code?: string };
    return obj.code || undefined;
  }
  return undefined;
}

export interface ConsolidatedPersonalityResult {
  /** The merged personality profile combining latest assessment + overrides */
  profile: PersonalityProfile | null;
  /** The raw latest TPS assessment (no overrides applied) */
  latestAssessment: any | null;
  /** ID of the assessment that's the source of the current profile */
  currentAssessmentId: string | null;
  /** Whether any admin overrides are applied */
  hasOverrides: boolean;
  /** Specific fields that have been overridden by admin */
  overriddenFields: string[];
  /** Fields whose value came from the canonical consensus record (not an admin override) */
  consensusFields: string[];
  /** True when the canonical record is newer than the source assessment */
  canonicalIsFresh: boolean;
  /** Loading state */
  loading: boolean;
  /** Total number of assessments */
  assessmentCount: number;
  /** ECR-R attachment detail from enrichment layers (if complete) */
  enrichedAttachment: Record<string, unknown> | null;
  /** Schwartz values profile from enrichment layers (if available) */
  valuesProfile: Record<string, unknown> | null;
}

/**
 * Hook that consolidates personality data from multiple sources:
 * 1. Latest TPS assessment profile
 * 2. Admin personality overrides
 * 
 * Priority: Override values > Latest assessment values
 * 
 * This provides a single source of truth for displaying personality data
 * across the app (home page, profile, dashboard).
 */
export function useConsolidatedPersonality(): ConsolidatedPersonalityResult {
  const { assessments, loading: assessmentsLoading } = useAssessments();
  const { override, loading: overrideLoading } = usePersonalityOverrides();
  const { upsertCanonical, canonical } = useCanonicalPersonality();
  const { data: confidenceReport } = useConfidenceReport();
  const { layers: enrichmentLayers, loading: enrichmentLoading } = useEnrichmentLayers();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const lastSyncedRef = useRef<string | null>(null);

  // Realtime subscription for cross-device canonical personality sync
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`canonical-personality-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_canonical_personality',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['canonical-personality'] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  // Listen for personality-data-changed events to invalidate caches
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.invalidateQueries({ queryKey: ['consolidated-personality'] });
      queryClient.invalidateQueries({ queryKey: ['canonical-personality'] });
      queryClient.invalidateQueries({ queryKey: ['unified-personality'] });
      queryClient.invalidateQueries({ queryKey: ['unified-personality-insights'] });
      queryClient.invalidateQueries({ queryKey: ['cross-feature-insights'] });
      queryClient.invalidateQueries({ queryKey: ['smart-recommendations'] });
      // Invalidate downstream consumers (career, liker, enrichment)
      queryClient.invalidateQueries({ queryKey: ['career-plans'] });
      queryClient.invalidateQueries({ queryKey: ['career-insights'] });
      queryClient.invalidateQueries({ queryKey: ['liker-recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['enrichment-layers'] });
      queryClient.invalidateQueries({ queryKey: ['data-sources-freshness'] });
    };
    window.addEventListener('personality-data-changed', handler);
    return () => window.removeEventListener('personality-data-changed', handler);
  }, [queryClient]);

  const result = useMemo(() => {
    // Find the latest TPS assessment (user-taken personality assessment)
    // Priority: v3 modular → source_type → legacy variant fallback
    const latestTPSAssessment = assessments?.find(
      (a) => a.profile?.v3Metadata || a.source_type === 'user_taken' || (!a.source_type && a.variant === 'full')
    ) || assessments?.[0];

    const baseProfile = latestTPSAssessment?.profile as PersonalityProfile | undefined;
    
    if (!baseProfile) {
      return {
        profile: null,
        latestAssessment: null,
        currentAssessmentId: null,
        hasOverrides: false,
        overriddenFields: [],
        consensusFields: [],
        canonicalIsFresh: false,
        assessmentCount: assessments?.length || 0,
        enrichedAttachment: null,
        valuesProfile: null,
      };
    }

    // Track which fields are overridden
    const overriddenFields: string[] = [];
    // Track which fields came from the canonical consensus record (NOT admin overrides)
    const consensusFields: string[] = [];

    // PROVENANCE GUARD: `last_updated_at` is NOT evidence of freshness — every
    // writer bumps it, so a stale type kept its "fresh" timestamp forever and
    // locked the displayed type in place. Canonical may only win when the
    // evidence behind it is at least as new as the source assessment:
    //   - `derived_from_at` (the assessment it was derived from).
    // Admin overrides are applied field-by-field below and must not make stale
    // canonical values authoritative for unrelated frameworks.
    // Rows with no provenance are treated as older than any newer assessment.
    const canonicalIsFresh = isCanonicalEvidenceFresh(
      (canonical as any)?.derived_from_at,
      latestTPSAssessment?.created_at || latestTPSAssessment?.updated_at,
    );
    
    // Apply overrides to create consolidated profile
    let consolidatedProfile = { ...baseProfile };

    // LIVE RECOMPUTATION: Always recompute bigFiveDetail from traitScores
    // using the current algorithm so old assessments benefit from scoring improvements
    if (baseProfile.traitScores && Object.keys(baseProfile.traitScores).length > 0) {
      const freshBigFive = calculateBigFiveEnhanced(baseProfile.traitScores);
      const freshMappings = { ...consolidatedProfile.mappings };
      freshMappings.bigFiveDetail = freshBigFive;

      // Sync flat bigFive scores (0-100 scale) from recomputed dimensions (1-10 scale)
      const flatBigFive: Record<string, number> = {};
      for (const [dimName, dimData] of Object.entries(freshBigFive.dimensions)) {
        const score10 = (dimData as any).score as number;
        // Convert 1-10 → 0-100 percentage
        flatBigFive[dimName] = Math.round(((score10 - 1) / 9) * 100);
      }
      freshMappings.bigFive = flatBigFive;

      // LIVE RECOMPUTATION: Recompute D&D Alignment from traitScores
      const freshAlignment = calculateAlignmentEnhanced(baseProfile.traitScores);
      freshMappings.dndAlignment = freshAlignment.alignment;
      freshMappings.alignmentDetail = freshAlignment;

      // LIVE RECOMPUTATION: Recompute Love Languages from traitScores
      const freshLoveLanguage = calculateLoveLanguages(baseProfile.traitScores);
      freshMappings.loveLanguage = freshLoveLanguage.primary;
      freshMappings.loveLanguageDetail = freshLoveLanguage;

      // LIVE RECOMPUTATION: Hogwarts / True Color / Temperament also need
      // to be re-derived on every read so users on legacy profiles get the
      // new softmax-based scoring instead of stale raw trait means.
      const freshHogwarts = calculateHogwartsHouse(baseProfile.traitScores);
      freshMappings.hogwartsHouse = freshHogwarts.house;
      freshMappings.hogwartsDetail = freshHogwarts;

      const freshTrueColor = calculateTrueColor(baseProfile.traitScores);
      freshMappings.trueColor = freshTrueColor.primary;
      freshMappings.trueColorDetail = freshTrueColor;

      const freshTemperament = calculateTemperament(baseProfile.traitScores);
      freshMappings.classicalTemperament = freshTemperament.temperament;
      freshMappings.temperamentDetail = freshTemperament;

      consolidatedProfile = { ...consolidatedProfile, mappings: freshMappings };
    }

    if (override) {
      // Deep clone mappings to avoid mutation
      const mappings = { ...consolidatedProfile.mappings };
      
      if (override.mbti_type && override.mbti_type !== mappings.mbti) {
        mappings.mbti = override.mbti_type;
        overriddenFields.push('mbti');
        
        // CASCADE: Derive Socionics from overridden MBTI if not explicitly set
        if (!override.socionics_type) {
          const derivedSocionics = MBTI_TO_SOCIONICS_MAPPING[override.mbti_type.toUpperCase()];
          if (derivedSocionics) {
            mappings.socionics = derivedSocionics;
          }
        }
        
        // CASCADE: Derive Keirsey from overridden MBTI
        const keirseyDetail = calculateKeirsey(override.mbti_type);
        mappings.keirseyDetail = keirseyDetail;
        
        // CASCADE: Update cognitive function stack for overridden MBTI type
        const rebuiltDetail = rebuildMbtiDetail(override.mbti_type, mappings.mbtiDetail, 'pinned');
        if (rebuiltDetail) mappings.mbtiDetail = rebuiltDetail;
      }
      
      if (override.enneagram_type && override.enneagram_type !== mappings.enneagram) {
        mappings.enneagram = override.enneagram_type;
        overriddenFields.push('enneagram');

        // CASCADE: rebuild BOTH Enneagram detail blocks from the effective type
        // so wing/tritype can never contradict the pinned core.
        const rebuiltEnn = rebuildEnneagramDetail(
          override.enneagram_type,
          (mappings as any).enneagramDetail,
          (mappings as any).enneagramDetails,
          'pinned',
        );
        if (rebuiltEnn) {
          (mappings as any).enneagramDetail = rebuiltEnn.detail;
          (mappings as any).enneagramDetails = rebuiltEnn.details;
        }
      }
      
      if (override.big_five_scores) {
        mappings.bigFive = override.big_five_scores;
        overriddenFields.push('bigFive');
        
        // CASCADE: Also patch bigFiveDetail dimension scores so the rich UI reflects overrides
        if (mappings.bigFiveDetail?.dimensions) {
          const detail = JSON.parse(JSON.stringify(mappings.bigFiveDetail));
          const overrideScores = override.big_five_scores as Record<string, any>;
          
          // Map flat override keys → HEXACO dimension names
          const OVERRIDE_TO_HEXACO: Record<string, string> = {
            extraversion: 'Extraversion',
            Extraversion: 'Extraversion',
            openness: 'Openness to Experience',
            Openness: 'Openness to Experience',
            'Openness to Experience': 'Openness to Experience',
            opennessToExperience: 'Openness to Experience',
            conscientiousness: 'Conscientiousness',
            Conscientiousness: 'Conscientiousness',
            agreeableness: 'Agreeableness',
            Agreeableness: 'Agreeableness',
            emotionality: 'Emotionality',
            Emotionality: 'Emotionality',
            neuroticism: 'Emotionality',
            Neuroticism: 'Emotionality',
          };
          
          for (const [key, value] of Object.entries(overrideScores)) {
            const hexacoDim = OVERRIDE_TO_HEXACO[key];
            if (hexacoDim && detail.dimensions[hexacoDim]) {
              // Normalize score: override values are 0-100, detail scores are 1-10
              const rawValue = typeof value === 'object' ? value.score : value;
              if (typeof rawValue === 'number') {
                const scaleScore = rawValue > 10 ? rawValue / 10 : rawValue;
                detail.dimensions[hexacoDim].score = Math.max(1, Math.min(10, scaleScore));
              }
            }
          }
          
          mappings.bigFiveDetail = detail;
        }
      }
      
      if (override.integral_level) {
        overriddenFields.push('integralLevel');
      }
      
      if (override.holland_code) {
        mappings.hollandCode = override.holland_code;
        // Sync hollandDetail for Enhanced Insights panel
        const letters = override.holland_code.split('');
        const HOLLAND_TYPE_NAMES: Record<string, string> = {
          R: 'Realistic', I: 'Investigative', A: 'Artistic',
          S: 'Social', E: 'Enterprising', C: 'Conventional'
        };
        if (mappings.hollandDetail) {
          mappings.hollandDetail = {
            ...mappings.hollandDetail,
            code: override.holland_code,
            primaryType: HOLLAND_TYPE_NAMES[letters[0]] || letters[0],
            secondaryType: letters[1] ? (HOLLAND_TYPE_NAMES[letters[1]] || letters[1]) : undefined,
          };
        }
        overriddenFields.push('hollandCode');
      }
      
      if (override.alignment) {
        mappings.dndAlignment = override.alignment;
        // Sync alignmentDetail axes to match the override string
        const parts = override.alignment.split(' ');
        const ethicalPosition = (parts[0] === 'True' ? 'Neutral' 
          : ['Lawful', 'Neutral', 'Chaotic'].includes(parts[0]) ? parts[0] : 'Neutral') as 'Lawful' | 'Neutral' | 'Chaotic';
        const moralPosition = (override.alignment === 'True Neutral' ? 'Neutral'
          : ['Good', 'Neutral', 'Evil'].includes(parts[1]) ? parts[1] : 'Neutral') as 'Good' | 'Neutral' | 'Evil';
        
        mappings.alignmentDetail = {
          confidence: mappings.alignmentDetail?.confidence ?? 0,
          alignment: override.alignment,
          ethicalAxis: {
            score: mappings.alignmentDetail?.ethicalAxis?.score ?? 0,
            reasoning: mappings.alignmentDetail?.ethicalAxis?.reasoning ?? '',
            position: ethicalPosition,
          },
          moralAxis: {
            score: mappings.alignmentDetail?.moralAxis?.score ?? 0,
            reasoning: mappings.alignmentDetail?.moralAxis?.reasoning ?? '',
            position: moralPosition,
          },
        };
        overriddenFields.push('alignment');
      }
      
      if (override.socionics_type) {
        mappings.socionics = override.socionics_type;
        overriddenFields.push('socionics');
      }

      // INLINE BIG FIVE COHERENCE: If MBTI override flips E/I, N/S, or J/P,
      // adjust the corresponding Big Five dimension so scores aren't contradictory.
      if (override.mbti_type && !override.big_five_scores) {
        const originalMbti = normalizeMBTI(baseProfile.mappings?.mbti)?.toUpperCase();
        const overrideMbti = override.mbti_type.toUpperCase();

        if (originalMbti && overrideMbti !== originalMbti) {
          const flippedEI = originalMbti[0] !== overrideMbti[0];
          const flippedNS = originalMbti[1] !== overrideMbti[1];
          const flippedJP = originalMbti[3] !== overrideMbti[3];

          // Helper to read a Big Five dimension score (1-10 scale)
          const getDimScore = (dimName: string): number | null => {
            const detail = mappings.bigFiveDetail?.dimensions as Record<string, any> | undefined;
            if (detail?.[dimName]?.score != null) return detail[dimName].score;
            // Fallback: try flat bigFive object (0-100 scale → convert to 1-10)
            const flat = mappings.bigFive as Record<string, any> | undefined;
            if (!flat) return null;
            for (const [k, v] of Object.entries(flat)) {
              if (k.toLowerCase().startsWith(dimName.toLowerCase().slice(0, 4))) {
                const raw = typeof v === 'object' ? v?.score : v;
                if (typeof raw === 'number') return raw > 10 ? raw / 10 : raw;
              }
            }
            return null;
          };

          // Helper to write adjusted score to both bigFive and bigFiveDetail
          const setDimScore = (dimName: string, flatKey: string, newScore: number) => {
            // Patch bigFiveDetail including facets
            if (mappings.bigFiveDetail?.dimensions) {
              const detail = JSON.parse(JSON.stringify(mappings.bigFiveDetail));
              if (detail.dimensions[dimName]) {
                const oldScore = detail.dimensions[dimName].score;
                detail.dimensions[dimName].score = newScore;
                
                // Additive shift preserves original facet-to-facet differentiation
                if (detail.dimensions[dimName].facets) {
                  const facets = detail.dimensions[dimName].facets;
                  const delta = newScore - oldScore;
                  
                  for (const facetKey of Object.keys(facets)) {
                    if (typeof facets[facetKey] === 'number') {
                      facets[facetKey] = Math.max(1, Math.min(10, 
                        Math.round((facets[facetKey] + delta) * 10) / 10
                      ));
                    }
                  }
                }
                
                mappings.bigFiveDetail = detail;
              }
            }
            // Patch flat bigFive
            if (mappings.bigFive && typeof mappings.bigFive === 'object') {
              (mappings.bigFive as Record<string, any>)[flatKey] = newScore * 10; // 1-10 → 0-100
            }
            overriddenFields.push(`bigFive_${flatKey}_coherence`);
          };

          // E/I → Extraversion
          if (flippedEI) {
            const score = getDimScore('Extraversion');
            const isNowExtrovert = overrideMbti[0] === 'E';
            if (score !== null) {
              if (isNowExtrovert && score < 5.0) setDimScore('Extraversion', 'extraversion', 5.8);
              if (!isNowExtrovert && score > 5.0) setDimScore('Extraversion', 'extraversion', 4.2);
            }
          }

          // N/S → Openness to Experience
          if (flippedNS) {
            const score = getDimScore('Openness to Experience');
            const isNowIntuitive = overrideMbti[1] === 'N';
            if (score !== null) {
              if (isNowIntuitive && score < 5.0) setDimScore('Openness to Experience', 'openness', 5.8);
              if (!isNowIntuitive && score > 5.0) setDimScore('Openness to Experience', 'openness', 4.2);
            }
          }

          // J/P → Conscientiousness
          if (flippedJP) {
            const score = getDimScore('Conscientiousness');
            const isNowJudger = overrideMbti[3] === 'J';
            if (score !== null) {
              if (isNowJudger && score < 5.0) setDimScore('Conscientiousness', 'conscientiousness', 5.8);
              if (!isNowJudger && score > 5.0) setDimScore('Conscientiousness', 'conscientiousness', 4.2);
            }
          }
        }
      }
      
      // Re-derive SLOAN from adjusted Big Five dimensions
      if (mappings.bigFiveDetail?.dimensions) {
        const dims = mappings.bigFiveDetail.dimensions;
        const getScore = (name: string) => dims[name]?.score ?? 5.0;
        const THRESHOLD = 50;
        
        const e = Math.min(100, Math.max(0, getScore('Extraversion') * 10));
        const em = Math.min(100, Math.max(0, getScore('Emotionality') * 10));
        const c = Math.min(100, Math.max(0, getScore('Conscientiousness') * 10));
        const a = Math.min(100, Math.max(0, getScore('Agreeableness') * 10));
        const o = Math.min(100, Math.max(0, getScore('Openness to Experience') * 10));
        
        const sLetter = e >= THRESHOLD ? 'S' : 'R';
        const lLetter = em >= THRESHOLD ? 'L' : 'C';
        const oLetter = c >= THRESHOLD ? 'O' : 'U';
        const aLetter = a >= THRESHOLD ? 'A' : 'E';
        const iLetter = o >= THRESHOLD ? 'I' : 'N';
        const sloanType = `${sLetter}${lLetter}${oLetter}${aLetter}${iLetter}`;
        
        mappings.sloan = sloanType;
        if (mappings.sloanDetail) {
          mappings.sloanDetail = {
            ...mappings.sloanDetail,
            type: sloanType,
            dimensions: {
              social_reserved: { letter: sLetter as 'S' | 'R', score: e, strength: Math.abs(e - THRESHOLD) },
              limbic_calm: { letter: lLetter as 'L' | 'C', score: em, strength: Math.abs(em - THRESHOLD) },
              organized_unstructured: { letter: oLetter as 'O' | 'U', score: c, strength: Math.abs(c - THRESHOLD) },
              accommodating_egocentric: { letter: aLetter as 'A' | 'E', score: a, strength: Math.abs(a - THRESHOLD) },
              inquisitive_noncurious: { letter: iLetter as 'I' | 'N', score: o, strength: Math.abs(o - THRESHOLD) },
            }
          };
        }
      }

      // RE-DERIVE INFERRED FRAMEWORKS when Big Five changed (via direct override or coherence)
      const bigFiveChanged = override.big_five_scores || overriddenFields.some(f => f.includes('bigFive') && f.includes('coherence'));
      if (bigFiveChanged && consolidatedProfile.traitScores && mappings.bigFiveDetail?.dimensions) {
        const adjustedTraits: TPSScores = { ...consolidatedProfile.traitScores };
        const dims = mappings.bigFiveDetail.dimensions;
        const origDims = baseProfile.mappings?.bigFiveDetail?.dimensions as Record<string, any> | undefined;

        // Compute Big Five deltas (new - original) on 1-10 scale
        const getDelta = (dimName: string): number => {
          const newScore = dims[dimName]?.score ?? 5;
          const oldScore = origDims?.[dimName]?.score ?? newScore;
          return newScore - oldScore;
        };

        // Big Five dimension → TPS trait correlations (weight applied as multiplier)
        const BIG_FIVE_TO_TRAITS: Record<string, { traits: string[]; weight: number; invert?: boolean }[]> = {
          'Extraversion': [{ traits: ['Assertive', 'Dynamic', 'Communal Navigate', 'Direct', 'Social'], weight: 0.5 }],
          'Emotionality': [{ traits: ['Stoic', 'Realistic', 'Self-Mastery'], weight: 0.4, invert: true }],
          'Agreeableness': [{ traits: ['Diplomatic', 'Passive', 'Responsive'], weight: 0.5 }],
          'Conscientiousness': [{ traits: ['Structured', 'Lawful', 'Analytical', 'Self-Mastery'], weight: 0.5 }],
          'Openness to Experience': [{ traits: ['Intuitive', 'Universal', 'Self-Aware', 'Independent'], weight: 0.5 }],
        };

        for (const [dimName, groups] of Object.entries(BIG_FIVE_TO_TRAITS)) {
          const delta = getDelta(dimName);
          if (Math.abs(delta) < 0.3) continue; // Skip trivial changes
          for (const group of groups) {
            const effectiveDelta = group.invert ? -delta : delta;
            for (const trait of group.traits) {
              if (adjustedTraits[trait] !== undefined) {
                adjustedTraits[trait] = Math.max(1, Math.min(10, adjustedTraits[trait] + effectiveDelta * group.weight));
              }
            }
          }
        }

        // Re-run inferred framework calculations with adjusted traits
        const newHogwarts = calculateHogwartsHouse(adjustedTraits);
        mappings.hogwartsHouse = newHogwarts.house;
        mappings.hogwartsDetail = newHogwarts;

        const newLoveLanguage = calculateLoveLanguages(adjustedTraits);
        mappings.loveLanguage = newLoveLanguage.primary;
        mappings.loveLanguageDetail = newLoveLanguage;

        const newTrueColor = calculateTrueColor(adjustedTraits);
        mappings.trueColor = newTrueColor.primary;
        mappings.trueColorDetail = newTrueColor;

        const newTemperament = calculateTemperament(adjustedTraits);
        mappings.classicalTemperament = newTemperament.temperament;
        mappings.temperamentDetail = newTemperament;

        overriddenFields.push('inferredFrameworks_rederived');
      }

      if (override.attachment_style) {
        mappings.attachmentStyle = override.attachment_style;
        const validStyles = ['anxious-preoccupied', 'dismissive-avoidant', 'fearful-avoidant', 'secure'] as const;
        if (mappings.attachmentDetail && validStyles.includes(override.attachment_style as any)) {
          mappings.attachmentDetail = {
            ...mappings.attachmentDetail,
            style: override.attachment_style as typeof validStyles[number]
          };
        }
        overriddenFields.push('attachmentStyle');
      }

      if (override.disc_profile) {
        mappings.disc = override.disc_profile;
        if (mappings.discDetail) {
          mappings.discDetail = { ...mappings.discDetail, primaryStyle: override.disc_profile };
        }
        overriddenFields.push('disc');
      }

      if (override.sloan_type) {
        mappings.sloan = override.sloan_type;
        if (mappings.sloanDetail) {
          mappings.sloanDetail = { ...mappings.sloanDetail, type: override.sloan_type };
        }
        overriddenFields.push('sloan');
      }

      // GENERIC PINS: frameworks without a dedicated override column store their
      // pinned value in `framework_pins` (Keirsey, Hogwarts, Love Language,
      // True Colors, Four Temperaments, …). A pinned framework stops being
      // recomputed exactly like a column-backed override.
      const genericPins = (override as any).framework_pins as Record<string, string> | null | undefined;
      if (genericPins && typeof genericPins === 'object') {
        const apply = (
          pinKey: string,
          field: string,
          detailField?: [string, string],
          overriddenName?: string,
        ) => {
          const raw = genericPins[pinKey];
          if (typeof raw !== 'string' || !raw.trim()) return;
          const value = raw.trim();
          (mappings as any)[field] = value;
          if (detailField) {
            const [detailKey, prop] = detailField;
            if ((mappings as any)[detailKey]) {
              (mappings as any)[detailKey] = { ...(mappings as any)[detailKey], [prop]: value };
            }
          }
          overriddenFields.push(overriddenName ?? pinKey);
        };

        apply('keirsey', 'keirsey', ['keirseyDetail', 'temperamentName'], 'keirsey');
        apply('hogwarts', 'hogwartsHouse', ['hogwartsDetail', 'houseName'], 'hogwarts');
        apply('loveLanguage', 'loveLanguage', ['loveLanguageDetail', 'primary'], 'loveLanguage');
        apply('trueColor', 'trueColor', ['trueColorDetail', 'primary'], 'trueColor');
        apply('temperament', 'classicalTemperament', ['temperamentDetail', 'temperamentName'], 'temperament');
      }

      consolidatedProfile = {
        ...consolidatedProfile,
        mappings
      };
    }

    // LAYER: Merge canonical MBTI from signal-blended resolution
    // Priority: Admin Override > Canonical (quiz/signal consensus) > Assessment Baseline
    // Only applies when canonical is at least as recent as the source assessment.
    if (canonicalIsFresh && canonical?.mbti && !overriddenFields.includes('mbti')) {
      const currentMbti = normalizeMBTI(consolidatedProfile.mappings?.mbti);
      const canonicalMbtiNorm = canonical.mbti.toUpperCase();
      if (currentMbti?.toUpperCase() !== canonicalMbtiNorm) {
        const mappings = { ...consolidatedProfile.mappings };
        mappings.mbti = canonicalMbtiNorm;

        // CASCADE: Derive Socionics from canonical MBTI
        const derivedSocionics = MBTI_TO_SOCIONICS_MAPPING[canonicalMbtiNorm];
        if (derivedSocionics) {
          mappings.socionics = derivedSocionics;
        }

        // CASCADE: Derive Keirsey from canonical MBTI
        const keirseyDetail = calculateKeirsey(canonicalMbtiNorm);
        mappings.keirseyDetail = keirseyDetail;

        // CASCADE: Update cognitive function stack
        const rebuiltCanonicalDetail = rebuildMbtiDetail(canonicalMbtiNorm, mappings.mbtiDetail, 'canonical');
        if (rebuiltCanonicalDetail) mappings.mbtiDetail = rebuiltCanonicalDetail;

        consensusFields.push('mbti_canonical');
        consolidatedProfile = { ...consolidatedProfile, mappings };
      }
    }

    // LAYER: MBTI derivative consistency.
    // Socionics / Keirsey / cognitive stack are deterministic functions of the
    // effective MBTI. A stored profile can carry pre-refit derivatives even when
    // its MBTI letters are already correct (the canonical mismatch cascade above
    // never fires in that case), so re-derive them whenever they disagree.
    // Explicit Socionics overrides always win.
    {
      const effectiveMbti = normalizeMBTI(consolidatedProfile.mappings?.mbti);
      const derived = deriveMbtiDerivatives(effectiveMbti);
      const socionicsPinned = overriddenFields.includes('socionics');
      if (derived) {
        const mappings = { ...consolidatedProfile.mappings };
        let changed = false;

        if (!socionicsPinned && isSocionicsStale(effectiveMbti, mappings.socionics)) {
          mappings.socionics = derived.socionics;
          if (mappings.socionicsDetail) {
            mappings.socionicsDetail = { ...mappings.socionicsDetail, type: derived.socionics } as any;
          }
          changed = true;
        }

        const currentKeirsey = (mappings.keirseyDetail as any)?.temperament;
        const keirseyPinned = overriddenFields.includes('keirsey');
        if (!keirseyPinned && (typeof currentKeirsey === 'string'
          ? currentKeirsey.toLowerCase() !== derived.keirsey.toLowerCase()
          : !mappings.keirseyDetail)) {
          mappings.keirseyDetail = calculateKeirsey(derived.mbti);
          changed = true;
        }

        const currentStack = [
          (mappings.mbtiDetail as any)?.cognitiveFunctions?.dominant?.function,
          (mappings.mbtiDetail as any)?.cognitiveFunctions?.auxiliary?.function,
          (mappings.mbtiDetail as any)?.cognitiveFunctions?.tertiary?.function,
          (mappings.mbtiDetail as any)?.cognitiveFunctions?.inferior?.function,
        ];
        const stackStale = derived.cognitiveStack.some((fn, i) => currentStack[i] !== fn);
        const detailTypeStale = normalizeMbtiCode((mappings.mbtiDetail as any)?.type) !== derived.mbti;
        if (stackStale || detailTypeStale) {
          const rebuilt = rebuildMbtiDetail(derived.mbti, mappings.mbtiDetail, 'derived');
          if (rebuilt) {
            mappings.mbtiDetail = rebuilt;
            changed = true;
          }
        }

        if (changed) {
          if (!consensusFields.includes('mbti_derivatives')) consensusFields.push('mbti_derivatives');
          consolidatedProfile = { ...consolidatedProfile, mappings };
        }
      }
    }

    // LAYER: Merge canonical Enneagram from signal-blended resolution
    if (canonicalIsFresh && canonical?.enneagram && !overriddenFields.includes('enneagram')) {
      const currentEnn = normalizeEnneagram(consolidatedProfile.mappings?.enneagram);
      if (currentEnn !== canonical.enneagram) {
        const mappings = { ...consolidatedProfile.mappings };
        mappings.enneagram = canonical.enneagram;

        // CASCADE: rebuild BOTH Enneagram detail blocks from the canonical type
        const rebuiltEnn = rebuildEnneagramDetail(
          canonical.enneagram,
          (mappings as any).enneagramDetail,
          (mappings as any).enneagramDetails,
          'canonical',
        );
        if (rebuiltEnn) {
          (mappings as any).enneagramDetail = rebuiltEnn.detail;
          (mappings as any).enneagramDetails = rebuiltEnn.details;
        }

        consensusFields.push('enneagram_canonical');
        consolidatedProfile = { ...consolidatedProfile, mappings };
      }
    }

    // LAYER: Merge canonical Big Five (signal-blended) into profile
    // Priority: Admin Override > Canonical (signal-blended) > Assessment Baseline
    if (
      canonicalIsFresh &&
      canonical?.big_five &&
      typeof canonical.big_five === 'object' &&
      !overriddenFields.includes('bigFive')
    ) {
      const canonicalBF = canonical.big_five as Record<string, number>;
      const baseBF = baseProfile.mappings?.bigFive as Record<string, number> | undefined;
      
      // Only merge if canonical differs from baseline (signals blended in)
      const hasSignalData = canonical.sources_used?.includes('signals') || 
        Object.entries(canonicalBF).some(([k, v]) => {
          const baseVal = baseBF?.[k];
          return typeof baseVal === 'number' && Math.abs(v - baseVal) > 0.5;
        });
      
      if (hasSignalData) {
        const mappings = { ...consolidatedProfile.mappings };
        mappings.bigFive = canonicalBF;
        
        // Also update bigFiveDetail dimensions if they exist
        if (mappings.bigFiveDetail?.dimensions) {
          const detail = JSON.parse(JSON.stringify(mappings.bigFiveDetail));
          const FLAT_TO_HEXACO: Record<string, string> = {
            extraversion: 'Extraversion', Extraversion: 'Extraversion',
            openness: 'Openness to Experience', Openness: 'Openness to Experience',
            conscientiousness: 'Conscientiousness', Conscientiousness: 'Conscientiousness',
            agreeableness: 'Agreeableness', Agreeableness: 'Agreeableness',
            emotionality: 'Emotionality', Emotionality: 'Emotionality',
            neuroticism: 'Emotionality', Neuroticism: 'Emotionality',
          };
          
          for (const [key, value] of Object.entries(canonicalBF)) {
            const hexacoDim = FLAT_TO_HEXACO[key];
            if (hexacoDim && detail.dimensions[hexacoDim]) {
              const rawValue = typeof value === 'number' ? value : 0;
              const scaleScore = rawValue > 10 ? rawValue / 10 : rawValue;
              detail.dimensions[hexacoDim].score = Math.max(1, Math.min(10, scaleScore));
            }
          }
          mappings.bigFiveDetail = detail;
        }
        
        // Re-derive SLOAN from updated Big Five
        if (mappings.bigFiveDetail?.dimensions) {
          const dims = mappings.bigFiveDetail.dimensions;
          const getScore = (name: string) => dims[name]?.score ?? 5.0;
          const THRESHOLD = 50;
          const e = Math.min(100, Math.max(0, getScore('Extraversion') * 10));
          const em = Math.min(100, Math.max(0, getScore('Emotionality') * 10));
          const c = Math.min(100, Math.max(0, getScore('Conscientiousness') * 10));
          const a = Math.min(100, Math.max(0, getScore('Agreeableness') * 10));
          const o = Math.min(100, Math.max(0, getScore('Openness to Experience') * 10));
          const sLetter = e >= THRESHOLD ? 'S' : 'R';
          const lLetter = em >= THRESHOLD ? 'L' : 'C';
          const oLetter = c >= THRESHOLD ? 'O' : 'U';
          const aLetter = a >= THRESHOLD ? 'A' : 'E';
          const iLetter = o >= THRESHOLD ? 'I' : 'N';
          mappings.sloan = `${sLetter}${lLetter}${oLetter}${aLetter}${iLetter}`;
        }
        
        consolidatedProfile = { ...consolidatedProfile, mappings };
      }
    }

    // Normalize all mappings to ensure they're strings (handles corrupted object data)
    const normalizedMappings = {
      ...consolidatedProfile.mappings,
      mbti: normalizeMBTI(consolidatedProfile.mappings?.mbti),
      enneagram: normalizeEnneagram(consolidatedProfile.mappings?.enneagram),
      dndAlignment: normalizeAlignment(consolidatedProfile.mappings?.dndAlignment),
      hollandCode: normalizeHollandCode(consolidatedProfile.mappings?.hollandCode),
    };

    let finalProfile: any = {
      ...consolidatedProfile,
      mappings: normalizedMappings
    };

    // Extract enrichment layer data for return
    const attachmentLayer = enrichmentLayers.find(l => l.layer === 'attachment' && l.status === 'complete');
    const valuesLayer = enrichmentLayers.find(l => l.layer === 'values' && (l.status === 'complete' || l.status === 'partial'));

    // If enrichment attachment layer is complete, prefer its ECR-R data over TPS-derived style
    if (attachmentLayer && finalProfile.mappings?.attachmentDetail) {
      const ecrData = attachmentLayer.data as Record<string, unknown>;
      if (ecrData.anxiety !== undefined && ecrData.avoidance !== undefined) {
        finalProfile = {
          ...finalProfile,
          mappings: {
            ...finalProfile.mappings,
            attachmentDetail: {
              ...finalProfile.mappings.attachmentDetail,
              ecrAnxiety: ecrData.anxiety,
              ecrAvoidance: ecrData.avoidance,
              enrichmentSource: 'ecr-r',
            },
          },
        };
      }
    }

    return {
      profile: finalProfile,
      latestAssessment: latestTPSAssessment,
      currentAssessmentId: latestTPSAssessment?.id || null,
      // Only a real admin override row counts as "verified" — canonical
      // consensus values are tracked separately in `consensusFields`.
      hasOverrides: !!override && overriddenFields.length > 0,
      overriddenFields,
      consensusFields,
      canonicalIsFresh,
      assessmentCount: assessments?.length || 0,
      enrichedAttachment: attachmentLayer?.data || null,
      valuesProfile: valuesLayer?.data || null,
    };
  }, [assessments, override, canonical, enrichmentLayers]);

  // Sync resolved personality to canonical table (fire-and-forget)
  useEffect(() => {
    const profile = result.profile;
    if (!profile?.mappings) return;

    const m = profile.mappings;
    const syncKey = JSON.stringify({
      mbti: m.mbti, enneagram: m.enneagram, socionics: m.socionics,
    });
    
    // Only sync when the resolved type actually changes
    if (syncKey === lastSyncedRef.current) return;
    lastSyncedRef.current = syncKey;

    // LOOP GUARD: never write a value back that we just read from canonical.
    // Without this, a stale canonical type gets re-upserted on every render and
    // stomps correct writes from the server-side aggregator.
    const canonicalMatchesResolved =
      !!canonical &&
      (canonical.mbti || null) === ((m.mbti as string) || null) &&
      (canonical.enneagram || null) === ((m.enneagram as string) || null) &&
      (canonical.socionics || null) === ((m.socionics as string) || null);
    const provenanceStamped = !!(canonical as any)?.derived_from_at;
    // Nothing to write when canonical already matches AND carries provenance.
    // Without provenance we still push once, to stamp it.
    if (canonicalMatchesResolved && provenanceStamped) return;
    // Canonical differs but is backed by evidence newer than the source
    // assessment — it already won the merge above, so there is nothing to push.
    if (canonical && result.canonicalIsFresh && provenanceStamped) return;

    const sourceSet = new Set<string>(
      Array.isArray(canonical?.sources_used) ? canonical.sources_used.filter((s): s is string => typeof s === 'string') : [],
    );
    sourceSet.add('tps');
    if (result.hasOverrides) sourceSet.add('override');
    const sources = Array.from(sourceSet);

    // Snapshot previous canonical before upserting (for timeline)
    const prevCanonical = canonical;

    upsertCanonical({
      mbti: m.mbti as string || null,
      enneagram: m.enneagram as string || null,
      big_five: m.bigFive as Record<string, number> || null,
      socionics: m.socionics as string || null,
      holland: (m.hollandCode as string) || null,
      alignment: (m.dndAlignment as string) || null,
      attachment: m.attachmentDetail?.style || null,
      disc: m.disc as string || null,
      sloan: m.sloan as string || null,
      integral_level: (m as any).integral?.level ?? null,
      // PROVENANCE: record which assessment this resolution came from so a
      // later render can tell fresh evidence from a stale leftover value.
      source_assessment_id: result.currentAssessmentId,
      derived_from_at:
        result.latestAssessment?.created_at ||
        result.latestAssessment?.updated_at ||
        new Date().toISOString(),
      // Prefer the canonical engine's calibrated value; fall back to the
      // legacy profile-level scalar only when the report isn't ready yet.
      confidence_score:
        confidenceReport?.value ?? (profile as any).confidence ?? 0,
      confidence_report: confidenceReport ?? null,
      sources_used: sources,
    }).then(() => {
      // Write timeline snapshot if type actually changed and we had a previous record
      if (user && prevCanonical && (prevCanonical.mbti !== (m.mbti as string || null) || prevCanonical.enneagram !== (m.enneagram as string || null))) {
        supabase.from('personality_timeline').insert({
          user_id: user.id,
          snapshot: {
            mbti: prevCanonical.mbti,
            enneagram: prevCanonical.enneagram,
            big_five: prevCanonical.big_five,
            socionics: prevCanonical.socionics,
            confidence_score: prevCanonical.confidence_score,
            sources_used: prevCanonical.sources_used,
          },
          trigger_source: 'client_sync',
          trigger_event: `type_change: ${prevCanonical.mbti || '?'}->${m.mbti || '?'}`,
        }).then(() => {});
      }
    }).catch(() => {
      // Silent fail — canonical sync is best-effort
    });
  }, [
    result.profile,
    result.hasOverrides,
    result.canonicalIsFresh,
    result.currentAssessmentId,
    result.latestAssessment,
    upsertCanonical,
    canonical,
    user,
    confidenceReport,
  ]);

  return {
    ...result,
    loading: assessmentsLoading || overrideLoading || enrichmentLoading
  };
}

/**
 * Helper function to check if an assessment matches the current consolidated profile
 */
export function isCurrentAssessment(
  assessmentId: string,
  currentAssessmentId: string | null
): boolean {
  return assessmentId === currentAssessmentId;
}
